import { Accordion, AccordionDetails, AccordionSummary, Box, Button, Grid, Tooltip, Typography } from "@mui/material";
import InfoIcon from "../../assets/client/fingertip-info.svg";
import Ask7GIcon from "../../assets/navigations/Ask7g-icon.svg";
import { extractDomainFromEmail, getPresetDateRange, TimelineItem, ContactPerson, parseSpeakers } from "../../utils/utils";
import { useContext, useState, useRef, useEffect } from "react";
import { GlobalContext } from "../../context/GlobalContext";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  getConcernDetailsBulletStyles,
  getConcernDomainGroupStyles,
  getConcernDomainTitleStyles,
  getConcernGroupAccordionStyles,
  getConcernGroupBadgeStyles,
  getConcernGroupChevronStyles,
  getConcernSubAccordionStyles,
  getConcernSubChevronStyles,
  getConcernSubDetailsStyles,
  getConcernSubIndexStyles,
  getConcernSubTopicStyles,
  getFactsProgressSegmentStyle,
  getFocusUrgencyBadgeStyles,
  getTrajectoryStyles,
  fingertipMetricViewStyles as styles,
} from "./FingertipMetricView.styles";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import PeopleIcon from "@mui/icons-material/People";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import InsightsIcon from "@mui/icons-material/Insights";
import InteractionsTimeline from "../Common/InteractionsTimeline";


type ConcernTopic = {
  topic?: string;
  summary?: string;
  domain_topic?: string;
  days_unresolved?: string;
  resolution_status?: string;
  number_of_mentions?: string | number;
  is_primary?: boolean;
  follow_up_seen?: string;
  current_status?: string;
  current_state?: string;
};

type ConcernGroupVariant = "primary" | "other";

type ConcernDomainTopic = {
  concern: ConcernTopic;
  displayIndex: number;
};

type ConcernDomainGroup = {
  domainTopic: string;
  topics: ConcernDomainTopic[];
};

const FingertipMetricsView = (
    {
      value,
      regenerate,
      onRegenerate,
      isGenerateLoading,
      isSlideOverOpen,
      tooltipNavigationState,
      clientName,
      timelineItems = [],
      monthlyScore = null,
      dateRange,
    }
    : {
      value: any;
      regenerate: any;
      onRegenerate: () => void;
      isGenerateLoading: boolean;
      isSlideOverOpen?: boolean;
      tooltipNavigationState?: Record<string, any>;
      clientName?: string | undefined;
      timelineItems?: TimelineItem[];
      monthlyScore?: Record<string, string> | null;
      dateRange?: { start_time: string; end_time: string };
}) => {
  const viewRef = useRef<HTMLDivElement | null>(null);
  const [ positiveExpanded, setPositiveExpanded] = useState<number | null>(null);
  const [primaryConcernsOpen, setPrimaryConcernsOpen] = useState(false);
  const [otherConcernsOpen, setOtherConcernsOpen] = useState(false);
  const [openConcernSubId, setOpenConcernSubId] = useState<string | null>(null);
  const [openTooltipKey, setOpenTooltipKey] = useState<string | null>(null);

  useEffect(() => {
    if (isSlideOverOpen === false) {
      setOpenTooltipKey(null);
    }
  }, [isSlideOverOpen]);
  const { accountDetails, impersonationDetails } = useContext(GlobalContext);
  const loggedInEmail = accountDetails?.email ?? "";
  const accountName = extractDomainFromEmail(loggedInEmail);
  const initialThreeMonthRange = dateRange || getPresetDateRange("last3MonthsFull");
  const isImpersonating = impersonationDetails?.isImpersonating === true;

  if (!value) return null;

  const {
    executive_summary,
    facts,
    topics,
    relationship,
    where_to_focus,
  } = value;
  const concerningTopics: ConcernTopic[] = Array.isArray(topics?.concerning_recurring_topics)
    ? topics?.concerning_recurring_topics
    : [];
  const primaryConcerns = concerningTopics.filter((topic) => topic?.is_primary);
  const otherConcerns = concerningTopics.filter((topic) => !topic?.is_primary);
  const contactGroups: { label: string; data: ContactPerson[] }[] = [
    { label: "Client Contacts:", data: parseSpeakers(facts?.people?.client_side?.all_speakers) },
    { label: "Account Contacts:", data: parseSpeakers(facts?.people?.account_side?.all_speakers) },
    { label: "Prospect Contacts:", data: parseSpeakers(facts?.people?.prospect_side?.all_speakers) },
    { label: "Vendor Contacts:", data: parseSpeakers(facts?.people?.vendor_side?.all_speakers) },
    { label: "Partner Contacts:", data: parseSpeakers(facts?.people?.partner_side?.all_speakers) },
    { label: "Guest Contacts:", data: parseSpeakers(facts?.people?.guest_side?.all_speakers) },
  ];

  const talkRatio = facts?.engagement_intensity?.talk_ratio;
  const [accountPct, clientPct, otherPct] = talkRatio
    ? talkRatio.split(":").map(Number)
    : [0, 0, 0];
  const executiveUrgencyLevel = where_to_focus?.executive_involvement?.urgency_level;
  const managerUrgencyLevel = where_to_focus?.manager_involvement?.urgency_level;

  const parseFocusContent = (text?: string) => {
    const normalized = (text || "").replace(/\s+/g, " ").trim();
    if (!normalized) {
      return { intro: "", points: [] as string[] };
    }

    const markerRegex = /\(\d+\)\s*/g;
    const numberedMatches = [...normalized.matchAll(/\(\d+\)\s*([\s\S]*?)(?=\s*\(\d+\)|$)/g)]
      .map((match) => match[1].trim())
      .filter(Boolean);

    if (numberedMatches.length > 0) {
      const firstMarker = markerRegex.exec(normalized);
      return {
        intro: firstMarker ? normalized.slice(0, firstMarker.index).trim() : "",
        points: numberedMatches,
      };
    }

    return {
      intro: "",
      points: normalized
        .split(/\n+|(?<=[.;])\s+/)
        .map((item) => item.trim())
        .filter(Boolean),
    };
  };

  const executiveFocus = parseFocusContent(where_to_focus?.executive_involvement?.reason);
  const managerFocus = parseFocusContent(where_to_focus?.manager_involvement?.reason);

  const formatPoint = (point: string) => {
    const trimmed = point.trim();
    if (!trimmed) return trimmed;
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
  };

  const toggleConcernSubAccordion = (id: string) => {
    setOpenConcernSubId((currentId) => (currentId === id ? null : id));
  };

  const getConcernTags = (concern: ConcernTopic) =>
    [
      concern?.follow_up_seen && { label: "Follow-Up Seen", value: concern?.follow_up_seen },
      concern?.current_status && { label: "Current Trend", value: concern?.current_status },
    ].filter(Boolean) as { label: string; value: string }[];

  const getConcernDomainGroups = (concerns: ConcernTopic[] = []): ConcernDomainGroup[] => {
    const domainMap = new Map<string, ConcernTopic[]>();

    concerns?.forEach((concern) => {
      const domainTopic = concern?.domain_topic?.trim() || "Other";
      const existingTopics = domainMap.get(domainTopic) || [];

      domainMap.set(domainTopic, [
        ...existingTopics,
        concern,
      ]);
    });

    let displayIndex = 0;

    return Array.from(domainMap, ([domainTopic, domainTopics]) => ({
      domainTopic,
      topics: domainTopics?.map((concern) => {
        displayIndex += 1;

        return {
          concern,
          displayIndex,
        };
      }),
    }));
  };

  const renderConcernSubAccordions = (
    concerns: ConcernTopic[],
    prefix: ConcernGroupVariant
  ) => {
    if (!concerns?.length) {
      return <Typography sx={styles.topicEmptyState}>—</Typography>;
    }

    const concernDomainGroups = getConcernDomainGroups(concerns);

    return (
      <Box sx={styles.concernDomainGroupsStack}>
        {concernDomainGroups?.map((domainGroup) => (
          <Box key={`${prefix}-${domainGroup?.domainTopic}`} sx={getConcernDomainGroupStyles(prefix)}>
            <Typography sx={getConcernDomainTitleStyles(prefix)}>
              {domainGroup?.domainTopic}
            </Typography>

            <Box sx={styles.concernSubStack}>
              {domainGroup?.topics?.map(({ concern, displayIndex }) => {
                const concernId = `${prefix}-${displayIndex - 1}`;
                const isOpen = openConcernSubId === concernId;
                const concernTags = getConcernTags(concern);
                const concernMetaItems = [
                  concern?.days_unresolved && { label: "Unresolved Days", value: concern?.days_unresolved },
                  concern?.resolution_status && { label: "Resolution Status", value: concern?.resolution_status },
                  concern?.number_of_mentions != null && { label: "Mentions", value: concern?.number_of_mentions },
                ].filter(Boolean) as { label: string; value: string | number }[];

                return (
                  <Accordion
                    key={concernId}
                    expanded={isOpen}
                    onChange={() => toggleConcernSubAccordion(concernId)}
                    disableGutters
                    elevation={0}
                    square={false}
                    sx={getConcernSubAccordionStyles(prefix, isOpen)}
                  >
                    <AccordionSummary sx={styles.concernSubSummary}>
                      <Box sx={getConcernSubIndexStyles(prefix, isOpen)}>
                        {displayIndex}
                      </Box>

                      <Box sx={styles.concernSubHeaderContent}>
                        <Typography sx={[...getConcernSubTopicStyles(isOpen), styles.concernSubTopicWithTags]}>
                          {concern?.topic || "Untitled concern"}
                        </Typography>

                        {concernTags?.length ? (
                          <Box sx={styles.concernTagsRow}>
                            {concernTags?.map((tag) => (
                              <Box component="span" key={tag?.label} sx={styles.concernTag}>
                                <Box component="span" sx={styles.concernTagLabel}>
                                  {tag?.label}:
                                </Box>
                                <Box component="span" sx={styles.concernTagValue}>
                                  {tag?.value}
                                </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : null}
                      </Box>

                      <Box sx={getConcernSubChevronStyles(prefix, isOpen)}>
                        <ExpandMoreIcon sx={styles.concernSubChevronIcon} />
                      </Box>
                    </AccordionSummary>

                    <AccordionDetails sx={getConcernSubDetailsStyles(prefix)}>
                      {Boolean(concernMetaItems?.length || concern?.summary || concern?.current_state) ? (
                        <Box component="ul" sx={styles.concernDetailsList}>
                          {concernMetaItems?.length ? (
                            <Box component="li" sx={styles.concernDetailsItem}>
                              <Box sx={getConcernDetailsBulletStyles(prefix)} />
                              <Box sx={styles.concernInlineMetaRow}>
                                {concernMetaItems?.map((detail) => (
                                  <Typography key={detail?.label} component="span" sx={styles.concernInlineMetaText}>
                                    <b>{detail?.label}:</b> {detail?.value}
                                  </Typography>
                                ))}
                              </Box>
                            </Box>
                          ) : null}

                          {concern?.summary ? (
                            <Box component="li" sx={styles.concernDetailsItem}>
                              <Box sx={getConcernDetailsBulletStyles(prefix)} />
                              <Typography sx={styles.concernDetailsText}>
                                <b>Summary:</b> {concern?.summary}
                              </Typography>
                            </Box>
                          ) : null}

                          {concern?.current_state ? (
                            <Box component="li" sx={styles.concernDetailsItem}>
                              <Box sx={getConcernDetailsBulletStyles(prefix)} />
                              <Typography sx={styles.concernDetailsText}>
                                <b>Current State:</b> {concern?.current_state}
                              </Typography>
                            </Box>
                          ) : null}
                        </Box>
                      ) : (
                        <Typography sx={styles.topicEmptyState}>—</Typography>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}
            </Box>
          </Box>
        ))}
      </Box>
    );
  };

  const renderConcernGroup = (
    variant: ConcernGroupVariant,
    label: string,
    subtitle: string,
    concerns: ConcernTopic[],
    expanded: boolean,
    onToggle: (expanded: boolean) => void
  ) => (
    <Accordion
      expanded={expanded}
      onChange={(_, isExpanded) => onToggle(isExpanded)}
      disableGutters
      elevation={0}
      square={false}
      sx={getConcernGroupAccordionStyles(variant, expanded)}
    >
      <AccordionSummary sx={styles.concernGroupSummary}>
        <Box sx={styles.concernGroupCopy}>
          <Typography sx={styles.concernGroupTitle}>{label}</Typography>
          {!expanded && (
            <Typography sx={styles.concernGroupSubtitle}>{subtitle}</Typography>
          )}
        </Box>

        <Box sx={styles.concernGroupActions}>
          <Box component="span" sx={getConcernGroupBadgeStyles(variant, expanded)}>
            {concerns?.length} Issues
          </Box>

          <Box sx={getConcernGroupChevronStyles(variant, expanded)}>
            <ExpandMoreIcon sx={styles.concernGroupChevronIcon} />
          </Box>
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={styles.concernGroupDetails}>
        {renderConcernSubAccordions(concerns, variant)}
      </AccordionDetails>
    </Accordion>
  );

  const trajectoryStyles = getTrajectoryStyles(
    relationship?.relationship_trajectory_flag
  );

  return (
    <>
    <Box ref={viewRef} sx={{ padding: "8px", overflowY: "auto", maxHeight: "calc(100vh - 235px)" }}>
        {regenerate && !isImpersonating && (
        <Box
            sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            backgroundColor: "#F4F1FF",
            border: "1px solid #C9B8FF",
            borderRadius: "8px",
            padding: "12px 16px",
            marginBottom: "16px",
            gap: "16px",
            }}
        >
            <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: "8px",
                flex: 1,
            }}
            >
            <img
                src={InfoIcon}
                alt="info"
            />
            <Typography sx={{ fontSize: "13px", color: "#2E2E2E" }}>
                We found new Interactions with the {clientName} Kindly Re-generate the Fingertip metrics.
            </Typography>
            </Box>

            <Button 
                variant="contained"
                className="fm-generate-btn"
                disabled={!regenerate}
                sx={{
                    opacity: !regenerate || isGenerateLoading ? 0.4 : 1,
                    transition: "opacity 0.3s ease",
                    }}
                onClick={onRegenerate}
            >
            <img
                src={Ask7GIcon}
                alt="Ask7G"
                className="fm-generate-btn-icon"
            />
            Regenerate
            </Button>
        </Box>
        )}
        
        <Grid container spacing={2} sx={styles.trendsGridContainer}>
          <Grid item xs={12} md={10} sx={styles.trendsGridItem}>
            <Box
              sx={{
                ...styles.summaryCard,
                ...styles.trendsCard,
              }}
            >
              <InteractionsTimeline
                items={timelineItems}
                startDate={initialThreeMonthRange.start_time}
                endDate={initialThreeMonthRange.end_time}
                mode="both"
                monthlyScore={monthlyScore}
                navigationState={tooltipNavigationState}
                isSlideOverOpen={isSlideOverOpen}
              />
            </Box>
          </Grid>

            <Grid item xs={12} md={2} sx={styles.trendsGridItem}>
              <Box
                sx={{
                  ...styles.summaryCard,
                  ...styles.trajectoryCard,
                }}
              >
                <Box sx={styles.trajectoryWrap}>
                  <Typography sx={styles.trajectoryTitle}>Trajectory:</Typography>
                  <Box component="span" sx={[styles.trajectoryBadge, trajectoryStyles]}>
                    {relationship?.relationship_trajectory || "—"}
                  </Box>
                </Box>
              </Box>
            </Grid>

            {/* ===== Executive Summary (Top Full Width) ===== */}
    <Grid item xs={12}>
      <Box sx={styles.summaryCard}>
        <Box sx={styles.summarySectionHeader}>
          <Box sx={[styles.summarySectionIcon, styles.executiveSummaryIcon]}>
            <TrendingUpIcon sx={{ fontSize: 18, color: "#2563EB" }} />
          </Box>

          <Typography sx={styles.summarySectionTitle}>
            Executive Summary
          </Typography>
        </Box>

        <Box sx={[styles.summaryContentPanel, styles.executiveSummaryPanel]}>
          <Box sx={styles.summaryContentSpacer}>

          </Box>

          <Typography sx={styles.summaryContentText}>
            {executive_summary?.summary || "—"}
          </Typography>
        </Box>
      </Box>
    </Grid>

            {/* ===== Interest Evolution ===== */}
    <Grid item xs={12}>
      <Box sx={styles.summaryCard}>
        <Box sx={styles.summarySectionHeader}>
          <Box sx={[styles.summarySectionIcon, styles.interestEvolutionIcon]}>
            <InsightsIcon sx={{ fontSize: 18, color: "#2563EB" }} />
          </Box>

          <Typography sx={styles.summarySectionTitle}>
            Interest Evolution
          </Typography>
        </Box>

        <Box sx={[styles.summaryContentPanel, styles.interestEvolutionPanel]}>
          <Box sx={styles.summaryContentSpacer} />

          <Typography sx={styles.summaryContentText}>
            {executive_summary?.client_interest_evolution || "—"}
          </Typography>
        </Box>
      </Box>
    </Grid>

            {/* ===== Where to Focus ===== */}
    <Grid item xs={12}>
      <Box sx={styles.summaryCard}>
      <Box>
        {/* Header */}
          <Box sx={styles.sectionHeader}>
            <Box sx={[styles.sectionIcon, styles.whereToFocusIcon]}>
            <GpsFixedIcon sx={{ fontSize: 18, color: "#7C3AED" }} />
          </Box>

          <Typography sx={styles.sectionTitle}>
            Where To Focus
          </Typography>
        </Box>

        {/* Content Wrapper */}
        <Box sx={styles.focusSectionsWrap}>
          
          {/* ===== Executive Involvement ===== */}
          <Box>
            <Box sx={styles.focusSubsectionHeader}>
              <Typography sx={styles.focusSubsectionTitle}>
                Executive Involvement
              </Typography>

              <Box sx={[styles.focusUrgencyBadge, getFocusUrgencyBadgeStyles(executiveUrgencyLevel)]}>
                {executiveUrgencyLevel}
              </Box>
            </Box>

            {executiveFocus.intro && (
              <Typography sx={styles.focusIntroText}>
                {executiveFocus.intro}
              </Typography>
            )}

            {executiveFocus.points.length ? (
              <Box component="ol" sx={styles.focusList}>
                {executiveFocus.points.map((point, idx) => (
                  <Typography
                    key={`exec-focus-${idx}`}
                    component="li"
                    sx={styles.focusListItem}
                  >
                    {formatPoint(point)}
                  </Typography>
                ))}
              </Box>
            ) : !executiveFocus.intro ? (
              <Typography sx={styles.focusBodyText}>—</Typography>
            ) : null}
          </Box>

          {/* ===== Manager Involvement ===== */}
          <Box>
            <Box sx={styles.focusSubsectionHeader}>
              <Typography sx={styles.focusSubsectionTitle}>
                Manager Involvement
              </Typography>

              <Box sx={[styles.focusUrgencyBadge, getFocusUrgencyBadgeStyles(managerUrgencyLevel)]}>
                {managerUrgencyLevel || "—"}
              </Box>
            </Box>

            {managerFocus.intro && (
              <Typography sx={styles.focusIntroText}>
                {managerFocus.intro}
              </Typography>
            )}

            {managerFocus.points.length ? (
              <Box component="ol" sx={styles.focusList}>
                {managerFocus.points.map((point, idx) => (
                  <Typography
                    key={`manager-focus-${idx}`}
                    component="li"
                    sx={styles.focusListItem}
                  >
                    {formatPoint(point)}
                  </Typography>
                ))}
              </Box>
            ) : !managerFocus.intro ? (
              <Typography sx={styles.focusBodyText}>—</Typography>
            ) : null}
          </Box>

        </Box>
      </Box>
      </Box>
    </Grid>

            {/* ===== Topics ===== */}

    {/* ===== Positive Topics ===== */}
    <Grid item xs={12}>
  <Box>
    <Box sx={styles.card}>

      {/* Header */}
    <Box sx={styles.topicSectionHeader}>
      <Box sx={[styles.topicSectionIcon, styles.positiveTopicIcon]}>
        <ThumbUpIcon sx={{ fontSize: 16, color: "#00A63E" }} />
      </Box>

      <Typography sx={styles.topicSectionTitle}>
        Positive Topics
      </Typography>
    </Box>

      {topics?.positive_recurring_topics?.length ? (
        <Box sx={styles.concernSubStack}>
        {topics?.positive_recurring_topics?.map((t: any, idx: number) => {
          const isOpen = positiveExpanded === idx;

          return (
            <Accordion
              key={`positive-${idx}`}
              expanded={isOpen}
              onChange={(_, expanded) => setPositiveExpanded(expanded ? idx : null)}
              disableGutters
              elevation={0}
              square={false}
              sx={getConcernSubAccordionStyles("positive", isOpen)}
            >
              <AccordionSummary sx={styles.concernSubSummary}>
                <Box sx={getConcernSubIndexStyles("positive", isOpen)}>
                  {idx + 1}
                </Box>

                <Typography sx={getConcernSubTopicStyles(isOpen)}>
                  {t?.topic}
                </Typography>

                <Box sx={getConcernSubChevronStyles("positive", isOpen)}>
                  <ExpandMoreIcon sx={styles.concernSubChevronIcon} />
                </Box>
              </AccordionSummary>

              <AccordionDetails sx={getConcernSubDetailsStyles("positive")}>
                <Box component="ul" sx={styles.concernDetailsList}>
                  {t?.number_of_mentions != null && (
                    <Box component="li" sx={styles.concernDetailsItem}>
                      <Box sx={getConcernDetailsBulletStyles("positive")} />
                      <Typography sx={styles.concernDetailsText}>
                        <b>Mentions:</b> {t?.number_of_mentions}
                      </Typography>
                    </Box>
                  )}

                  {t?.summary && (
                    <Box component="li" sx={styles.concernDetailsItem}>
                      <Box sx={getConcernDetailsBulletStyles("positive")} />
                      <Typography sx={styles.concernDetailsText}>
                        <b>Summary:</b> {t?.summary}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          );
        })}
        </Box>
      ) : (
        <Typography sx={styles.topicEmptyState}>—</Typography>
      )}
    </Box>
    </Box>
</Grid>

    {/* ===== Concerning Topics ===== */}
    <Grid item xs={12}>
  <Box>
    
    <Box sx={styles.card}>

{/* Header */}
    <Box sx={styles.topicSectionHeader}>
      <Box sx={[styles.topicSectionIcon, styles.concerningTopicIcon]}>
        <WarningAmberIcon sx={styles.concerningTopicIconGraphic} />
      </Box>

      <Typography sx={styles.topicSectionTitle}>
        Concerning Topics
      </Typography>
    </Box>

      {concerningTopics?.length ? (
        <Box sx={styles.concernGroupsStack}>
          {renderConcernGroup(
            "primary",
            "Primary Concerns",
            "Critical issues requiring immediate attention",
            primaryConcerns,
            primaryConcernsOpen,
            setPrimaryConcernsOpen
          )}
          {renderConcernGroup(
            "other",
            "Other Concerns",
            "Additional concerns for consideration",
            otherConcerns,
            otherConcernsOpen,
            setOtherConcernsOpen
          )}
        </Box>
      ) : (
        <Typography sx={styles.topicEmptyState}>—</Typography>
      )}
    </Box>
  </Box>
</Grid>

   {/* ===== Relationship ===== */}
    <Grid item xs={12}>
      <Box sx={styles.card}>
        {/* Header */}
        <Box sx={styles.sectionHeader}>
          {/* Icon */}
          <Box sx={[styles.sectionIcon, styles.relationshipIcon]}>
            <PeopleIcon sx={styles.relationshipIconGraphic} />
          </Box>

          {/* Title + Badge */}
          <Box sx={styles.relationshipTitleRow}>
            <Typography sx={styles.relationshipTitle}>
              Relationship -
            </Typography>

            <Box component="span" sx={[styles.trajectoryHighlight, trajectoryStyles]}>
              {relationship?.relationship_trajectory || "—"}
            </Box>
          </Box>
        </Box>

        {/* Content */}
        <Box sx={styles.relationshipContent}>
          <Typography sx={styles.focusBodyText}>
            <Box component="span" sx={styles.relationshipLabel}>
              New Stakeholder:
            </Box>{" "}
            {relationship?.new_stakeholder_entry || "None"}
          </Typography>

          <Typography sx={styles.focusBodyText}>
            <Box component="span" sx={styles.relationshipLabel}>
              Engagement Pattern:
            </Box>{" "}
            {relationship?.engagement_pattern || "—"}
          </Typography>
        </Box>
      </Box>
    </Grid>


    {/* ===== Facts ===== */}
    <Grid item xs={12}>
      <Box sx={styles.card}>
        {/* Header */}
        <Box sx={styles.factsHeader}>
          <Box sx={[styles.sectionIcon, styles.factsIcon]}>
            <LightbulbIcon sx={styles.factsIconGraphic} />
          </Box>

          <Typography sx={styles.factsSectionTitle}>
            Quick Facts
          </Typography>
        </Box>

        {/* Talk Ratio */}
        <Box sx={styles.factsTalkRatioSection}>
          <Typography sx={styles.factsSubheading}>
            Talk Ratio:
          </Typography>

          {talkRatio ? (
            <>
              {/* Labels */}
              <Box sx={styles.factsRatioLabels}>
                <Box sx={styles.factsRatioLabelGroup}>
                  <Box sx={[styles.factsRatioDot, styles.factsRatioDotAccount]} />
                  <Typography sx={styles.factsRatioText}>
                    {accountName} <b>({accountPct}%)</b>
                  </Typography>
                </Box>

                <Typography sx={styles.factsRatioSeparator}>|</Typography>

                <Box sx={styles.factsRatioLabelGroup}>
                  <Box sx={[styles.factsRatioDot, styles.factsRatioDotClient]} />
                  <Typography sx={styles.factsRatioText}>
                    {clientName} <b>({clientPct}%)</b>
                  </Typography>
                </Box>

                <Typography sx={styles.factsRatioSeparator}>|</Typography>

                <Box sx={styles.factsRatioLabelGroup}>
                  <Box sx={[styles.factsRatioDot, styles.factsRatioDotOther]} />
                  <Typography sx={styles.factsRatioText}>
                    Other <b>({otherPct}%)</b>
                  </Typography>
                </Box>
              </Box>

              {/* Progress Bar */}
              <Box sx={styles.factsProgressBar}>
                <Box sx={getFactsProgressSegmentStyle(accountPct, "#9333EA")} />
                <Box sx={getFactsProgressSegmentStyle(clientPct, "#2563EB")} />
              </Box>
            </>
          ) : (
            "—"
          )}
        </Box>

        {/* Metrics Cards */}
        <Box sx={styles.factsMetricCards}>
          <Box sx={[styles.factsMetricCard, styles.factsMetricCardDuration]}>
            <Typography sx={styles.factsMetricLabel}>
              Avg Call Duration
            </Typography>
            <Typography sx={styles.factsMetricValue}>
              {facts?.meeting_cadence?.avg_call_duration || "—"}
            </Typography>
          </Box>

          <Box sx={[styles.factsMetricCard, styles.factsMetricCardInteraction]}>
            <Typography sx={styles.factsMetricLabel}>
              Last Interaction
            </Typography>
            <Typography sx={styles.factsMetricValue}>
              {facts?.meeting_cadence?.last_interaction_date || "—"}
            </Typography>
          </Box>
        </Box>

        {/* Contact Chips */}
        {contactGroups.map(
          (group, idx) =>
            group.data?.length > 0 && (
              <Box key={idx} sx={styles.factsContactGroup}>
                <Typography sx={styles.factsContactLabel}>
                  {group.label}
                </Typography>

                <Box sx={styles.factsContactChips}>
                  {group.data.map((contact: ContactPerson, i: number) => {
                    const chipKey = `${idx}-${i}`;
                    const email = contact.email;

                    const chip = (
                      <Box
                        key={chipKey}
                        sx={{
                          ...styles.factsContactChip,
                          ...(email
                            ? {
                                "&:hover": {
                                  backgroundColor: "#E2E2E2",
                                },
                              }
                            : {}),
                        }}
                      >
                        {contact.name}
                      </Box>
                    );

                    return email ? (
                      <Tooltip
                        key={chipKey}
                        title={email}
                        arrow
                        placement="top"
                        open={openTooltipKey === chipKey}
                        onOpen={() => setOpenTooltipKey(chipKey)}
                        onClose={() => setOpenTooltipKey(null)}
                      >
                        {chip}
                      </Tooltip>
                    ) : (
                      chip
                    );
                  })}
                </Box>
              </Box>
            )
        )}
      </Box>
    </Grid>
  </Grid>
    </Box>
    </>
  );
};

export default FingertipMetricsView;
